import React, { useState } from "react";
import { Link } from "react-router-dom";
import validator from "validator";
import { User, Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import { Input } from "../../components/ui/input";
import CircularProgress from "@mui/material/CircularProgress";
import { Button } from "../../components/ui/button";
import { logUserActionBackground, USER_ACTION_EVENTS } from "../../services/userActionLog";

const RegisterStep1 = (props) => {
  const authInputClassName =
    "pl-40 bg-white text-[#0f172a] border-gray-300 placeholder:text-gray-500 focus-visible:ring-[#2980ba]";
  const [showPassword, setShowPassword] = useState(false);
  const stepId = 1;
  const {
    formState,
    handleInputChange,
    setErrorMessage,
    errorMessage,
    goNext,
    error,
    emailError,
    loading
  } = props;

  const handleInputChangeTrimmed = (e) => {
    const { name, value } = e.target;
    const trimmedValue =
      name === "firstname" || name === "lastname" ? value.replace(/^\s+|\s+$/g, "") : value;
    handleInputChange({ target: { name, value: trimmedValue } });
  };

  const validateAndGoNext = () => {
    const { firstname, lastname, email, password, confirmpassword } = formState;
    const newErrors = {};

    if (!firstname?.trim()) newErrors.firstname = "First name is required.";
    if (!lastname?.trim()) newErrors.lastname = "Last name is required.";

    if (!email?.trim()) {
      newErrors.email = "Email is required.";
    } else if (!validator.isEmail(email)) {
      newErrors.email = "Email is not valid.";
    }

    if (!password) {
      newErrors.password = "Password is required.";
    } else if (password.length < 6) {
      newErrors.password = "Password should be at least 6 characters.";
    } else if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(password)) {
      newErrors.password = "Password must contain at least one letter and one number.";
    }

    if (!confirmpassword) {
      newErrors.confirmpassword = "Confirm Password is required.";
    } else if (password !== confirmpassword) {
      newErrors.confirmpassword = "Passwords do not match.";
    }

    setErrorMessage(newErrors);

    if (Object.keys(newErrors).length === 0) {
      logUserActionBackground({
        userId: "pre-register-step1",
        eventName: USER_ACTION_EVENTS.REGISTER_STEP1,
        email: email?.trim().toLowerCase(),
        displayName: `${firstname?.trim()} ${lastname?.trim()}`,
      });
      goNext();
    }
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    validateAndGoNext();
  };

  return (
    <form onSubmit={handleStep1Submit} className="space-y-5 sm:space-y-6 w-full px-4 sm:px-0">
      <div className="space-y-2">
        <label htmlFor="firstname" className="block text-sm font-medium">
          First Name <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            fullWidth={true}
            disableUnderline={true}
            onChange={handleInputChangeTrimmed}
            value={formState.firstname ? formState.firstname : ""}
            className={authInputClassName}
            name="firstname"
            id="firstname"
            placeholder="First Name"
            tabIndex={1}
            data-testid="reg-firstname"
          />
          {errorMessage.firstname && (
            <div className="small error color-danger">
              {errorMessage.firstname}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <label htmlFor="lastname" className="block text-sm font-medium">
          Last Name <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            fullWidth={true}
            disableUnderline={true}
            onChange={handleInputChangeTrimmed}
            value={formState.lastname ? formState.lastname : ""}
            className={authInputClassName}
            name="lastname"
            id="lastname"
            placeholder="Last Name"
            tabIndex={2}
            data-testid="reg-lastname"
          />
        </div>
        {errorMessage.lastname && (
          <div className="small error color-danger">
            {errorMessage.lastname}
          </div>
        )}
      </div>
      <div className="space-y-2">
        <label htmlFor="email" className="block text-sm font-medium">
          Email Address <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            fullWidth={true}
            disableUnderline={true}
            onChange={handleInputChange}
            value={formState.email ? formState.email : ""}
            autoComplete="email"
            className={authInputClassName}
            name="email"
            id="email"
            placeholder="Email"
            tabIndex={3}
            data-testid="reg-email"
          />
        </div>
        {errorMessage.email && (
          <div className="small error color-danger">{errorMessage.email}</div>
        )}
        {error !== "" && error === "Email is already registered" && <div className="small error color-danger">{error}</div>}
        {emailError !== "" && <div className="small error color-danger">{emailError}</div>}
      </div>
      <div className="space-y-2">
        <label htmlFor="password" className="block text-sm font-medium">
          Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            fullWidth={true}
            disableUnderline={true}
            onChange={handleInputChange}
            value={formState.password ? formState.password : ""}
            className={authInputClassName}
            name="password"
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            autoComplete="new-password"
            tabIndex={4}
            data-testid="reg-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
        {errorMessage.password && (
          <div className="small error color-danger">
            {errorMessage.password}
          </div>
        )}
      </div>
      <div className="space-y-2">
        <label htmlFor="confirmpassword" className="block text-sm font-medium">
          Confirm Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            fullWidth={true}
            disableUnderline={true}
            onChange={handleInputChange}
            value={formState.confirmpassword ? formState.confirmpassword : ""}
            className={authInputClassName}
            name="confirmpassword"
            id="confirmpassword"
            autoComplete="new-password-confirm"
            placeholder="Confirm Password"
            type={showPassword ? "text" : "password"}
            tabIndex={5}
            data-testid="reg-confirmpassword"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
        {errorMessage.confirmpassword && (
          <div className="small error color-danger">
            {errorMessage.confirmpassword}
          </div>
        )}
      </div>
      <Button
        type="submit"
        className="w-full"
        size="lg"
        tabIndex={6}
        data-testid="reg-submit-step1"
      >
        {loading ? (
          <CircularProgress size={28} style={{ color: "white" }} />
        ) : (
          <>
            Complete Setup <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>
      <div className="text-center text-sm text-gray-500">
        Already have an account?{" "}
        <Link
          to="/login"
          className="text-green-600 hover:text-green-800 font-medium"
        >
          Log in
        </Link>
      </div>
    </form>
  );
};

export default RegisterStep1;
